import { request } from "./client";
import type { VoiceBrief } from "./voices";

export interface PlaybookSummary {
  id: string;
  name: string;
  description: string;
  greeting: string;
  target_count: number;
  phase_count: number;
  voice_id: string;
  total_sessions: number;
  dialed_count: number;
  answered_count: number;
  success_count: number;
  last_active: number | null;
  voices: VoiceBrief[];
}

export interface PlaybookListResponse {
  playbooks: PlaybookSummary[];
}

export function listPlaybooks(): Promise<PlaybookListResponse> {
  return request("/playbooks");
}

export interface PlaybookDetail {
  id: string;
  name: string;
  content: string;
  voice_id: string;
  pre_opening: string;
  pre_opening_gap_ms: number;
  ambient_id: string;
  ambient_gain_db: number;
  ab_variant_b_id: string;
  ab_distribution_a: number;
}

export interface PlaybookMetaPatch {
  display_name?: string;
  voice_id?: string;
  app?: string;
  cache_limit?: number;
  pre_opening?: string;
  pre_opening_gap_ms?: number;
  ambient_id?: string;
  ambient_gain_db?: number;
  ab_variant_b_id?: string;
  ab_distribution_a?: number;
}

export function getPlaybook(id: string): Promise<PlaybookDetail> {
  return request(`/playbooks/${id}`);
}

export interface PlaybookVersion {
  id: number;
  content_hash: string;
  created_at: number;
}

export function listPlaybookVersions(playbookId: string): Promise<{ versions: PlaybookVersion[] }> {
  return request(`/playbooks/${playbookId}/versions`);
}

export function getPlaybookVersion(playbookId: string, versionId: number): Promise<{ id: number; content: string }> {
  return request(`/playbooks/${playbookId}/versions/${versionId}`);
}

export function getPlaybookParams(playbookId: string): Promise<{ params: string[] }> {
  return request(`/playbooks/${playbookId}/params`);
}

export interface PlaybookMeta {
  id: string;
  display_name: string;
  voice_id: string;
  app: string;
}

export function patchPlaybook(
  id: string,
  patch: PlaybookMetaPatch,
): Promise<PlaybookMeta> {
  return request(`/playbooks/${id}`, {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(patch),
  });
}

export function updatePlaybookContent(id: string, content: string): Promise<{ ok: boolean }> {
  return request(`/playbooks/${id}/content`, {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ content }),
  });
}

export interface GeneratePlaybookRequest {
  materials: string[];
  parameterize?: boolean;
  params?: string[];
  app?: string;
}

export interface GeneratePlaybookResponse {
  content: string;
  params: string[];
}

export function generatePlaybook(input: GeneratePlaybookRequest): Promise<GeneratePlaybookResponse> {
  return request("/playbooks/generate", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(input),
  });
}

export function createPlaybook(input: {
  id?: string;
  display_name: string;
  content: string;
  voice_id?: string;
  app?: string;
}): Promise<{ id: string }> {
  return request("/playbooks", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(input),
  });
}

export function deletePlaybook(id: string): Promise<{ id: string }> {
  return request(`/playbooks/${id}`, { method: "DELETE" });
}

export interface ParsePreview {
  greeting: string;
  knowledge_base: string;
  phases: { number: number; trigger: string; content: string }[];
  targets: string[];
  base_content: string;
}

export function parsePreview(content: string): Promise<ParsePreview> {
  return request("/playbooks/parse-preview", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ content }),
  });
}
